import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useToolConfig } from '@/hooks/useToolConfig';
import { useSessionPersistence } from '@/hooks/useSessionPersistence';
import { useVersionsHydration } from '@/hooks/useVersionsHydration';
import { useSessionStore } from '@/stores/session';
import { mintSessionFolder } from '@/lib/sessionFolder';
import { LoadingView } from '@/components/LoadingView';
import { ErrorView } from '@/components/ErrorView';
import { PasswordPrompt } from '@/components/PasswordPrompt';
import { SessionRestorePrompt } from '@/components/SessionRestorePrompt/SessionRestorePrompt';
import { SessionLockedPrompt } from '@/components/SessionLockedPrompt/SessionLockedPrompt';
import { Button } from '@/components/ui/Button';
import { BrowserGate } from '@/components/BrowserGate/BrowserGate';
import { isSupportedBrowser } from '@/lib/browserSupport';
import { withSearch } from '@/lib/nav';

/** Hard-block non-Chromium browsers on the talent-facing pages before any
 *  config loads or WebAudio hooks mount. /admin lives outside this layout and
 *  stays usable in Safari. Browser support is invariant for the session, so
 *  this wrapper never mounts ToolLayoutInner on an unsupported browser (no
 *  hook-order concerns). See `src/lib/browserSupport.ts`. */
export function ToolLayout() {
  if (!isSupportedBrowser()) return <BrowserGate />;
  return <ToolLayoutInner />;
}

/** Wraps any route that needs a resolved tool config. Handles loading,
 *  password-prompt, and error states once — child routes render only when
 *  config is ready and applied to the session store. */
function ToolLayoutInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang')?.toUpperCase() ?? undefined;
  const { status, submitPassword, lastAttemptFailed, passwordChanged } = useToolConfig(id, lang);

  // IndexedDB persistence key: `${id}:${lang}` for multilingual projects so
  // each language gets its own isolated session slot (they must not clobber
  // each other), plain `id` otherwise — byte-identical to pre-multilingual
  // behavior. `lang` is known from the URL alone (independent of config
  // load), so this can't wait for `status.kind === 'ready'` — the
  // persistence hook mounts before that. Accepted edge case: a STANDARD
  // (non-multilingual) tool with an accidentally-appended `?lang=` gets
  // bounced to a fresh, empty slot rather than reusing the tool's real
  // session — nothing is lost, the original slot under plain `id` is still
  // there and comes back the moment the stray param is gone.
  const persistKey = id && lang ? `${id}:${lang}` : id;

  // Wire browser-side session persistence as soon as we know the tool ID.
  // The hook returns a gate: if IndexedDB holds a meaningful prior
  // session, it asks the user to choose before hydrating. Routing into
  // the app has to wait on that choice.
  const sessionGate = useSessionPersistence(persistKey);

  // Cold-start hydration of `S.versions` from KV. Fires once `(toolId,
  // sessionFolder)` are both known — i.e. after the user picks "Continue"
  // on the restore prompt and `applyStored` has populated sessionFolder.
  // Mirrors vanilla's `await fetchVersions()` call at legacy:3355, just
  // driven by an effect dependency instead of an imperative await.
  const sessionFolder = useSessionStore((s) => s.sessionFolder);
  const userName = useSessionStore((s) => s.name);
  useVersionsHydration(id, sessionFolder);

  // Lazy mint for pre-fix sessions: commit 314cd87 added a Welcome-time
  // mint, but sessions hydrated from IndexedDB BEFORE that fix landed
  // carry `sessionFolder: ''` even though they have a `name` (i.e.
  // `hasMeaningfulState` returned true and the user clicked "Continue").
  // Welcome doesn't run on resume, so without this the Upload Version
  // button stays gated forever (`uploadDisabled = !sessionFolder` in
  // TTSView/STSView). Fires only when both gates are satisfied: the
  // session has resolved AND we have a name to anchor the folder to.
  // See `src/lib/sessionFolder.ts` for the format.
  useEffect(() => {
    if (sessionGate.kind !== 'hydrated') return;
    if (sessionFolder) return;
    const trimmed = userName.trim();
    if (!trimmed) return;
    const { folder, shortId } = mintSessionFolder(trimmed);
    useSessionStore.getState().setSessionFolder(folder, shortId);
  }, [sessionGate.kind, sessionFolder, userName]);

  // Deep-link guard: a talent who opens `/:id/generate` (or `/upload`) directly
  // — copied URL, fresh browser profile, cleared data — never passed through
  // Welcome, so `name` AND `sessionFolder` are both empty and the lazy-mint
  // above can't fire (no name to anchor the folder to). The Generation UI would
  // run fine but the Upload button stays gated forever ('Session is still
  // initialising', `uploadDisabled = !sessionFolder`). Bounce back to Welcome so
  // the talent enters a name → mints the folder → can actually upload.
  useEffect(() => {
    if (sessionGate.kind !== 'hydrated') return;
    if (!id) return;
    if (sessionFolder || userName.trim()) return; // has a session or a name
    const welcome = `/${id}`;
    if (location.pathname !== welcome && location.pathname !== `${welcome}/`) {
      navigate(withSearch(welcome, location.search), { replace: true });
    }
  }, [sessionGate.kind, sessionFolder, userName, id, location.pathname, location.search, navigate]);

  if (status.kind === 'loading') return <LoadingView message="Loading project…" />;
  if (status.kind === 'password-required') {
    return (
      <PasswordPrompt
        onSubmit={submitPassword}
        hint={
          lastAttemptFailed
            ? 'Wrong password — try again'
            : passwordChanged
              ? 'The project password was changed — please enter the new one. Your work is still here.'
              : undefined
        }
      />
    );
  }
  if (status.kind === 'error') {
    return (
      <ErrorView
        title="Can’t open this project"
        message={status.message}
        status={status.status}
      />
    );
  }

  // Config is ready — but we may still be reading IndexedDB. Keep the
  // loading chrome until the gate resolves so the initial render doesn't
  // flash Welcome and then get redirected.
  if (sessionGate.kind === 'checking') {
    return <LoadingView message="Loading project…" />;
  }

  // Ein anderer Tab hält diese Session (Audit M8a) — hier nichts rendern,
  // das schreiben könnte.
  if (sessionGate.kind === 'locked') {
    return <SessionLockedPrompt onTakeOver={sessionGate.onTakeOver} />;
  }

  // IndexedDB konnte nicht gelesen werden (Audit M8b): fail-closed, kein
  // Autosave, bis ein Retry gelingt — sonst überschreibt der leere Store die
  // gespeicherte Arbeit.
  if (sessionGate.kind === 'error') {
    return (
      <ErrorView
        title="Couldn’t load your saved work"
        message={`Your browser’s local storage didn’t respond. Nothing has been deleted — please try again. (${sessionGate.message})`}
        action={
          <Button variant="primary" size="sm" onClick={sessionGate.onRetry}>
            <i className="ph-bold ph-arrow-clockwise" aria-hidden="true" />
            Try again
          </Button>
        }
      />
    );
  }

  if (sessionGate.kind === 'restore-prompt') {
    return (
      <SessionRestorePrompt
        sessionName={sessionGate.sessionName}
        onContinue={() => {
          sessionGate.onContinue();
          if (id) navigate(withSearch(`/${id}/generate`, location.search), { replace: true });
        }}
        onStartFresh={() => {
          sessionGate.onStartFresh();
          if (id) navigate(withSearch(`/${id}`, location.search), { replace: true });
        }}
      />
    );
  }

  return <Outlet />;
}
